import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Grid from '@mui/material/Unstable_Grid2';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DashboardIcon from '@mui/icons-material/Dashboard';
import {
    BACK_OFFICE,
    BUDGET,
    DECISION,
    DECISION_USER,
    HUMAN_RESOURCES,
    HUMAN_RESOURCES_USER,
    SUPPORT,
} from 'business/constants';
import {
    trackEvent,
    trackEventBudget,
    TRACKABLE_EVENTS,
} from 'helpers/trackerHelpers';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    useClientHasModule,
    useIsEssential,
    useIsExploreUser,
} from 'hooks/client';
import {
    useHasAccessBudgetsGestion,
    useHasAccessCadrageGestion,
    useHasDafAccess,
    useIsAdminCategories,
    useUserHasRole,
} from 'hooks/user';
import { Home } from '@mui/icons-material';
import {
    BUDGET_HOME_PAGE_URL,
    CHARTS_TEMPLATES_PATHNAME,
    BUDGET_OVERVIEW,
    DASHBOARDS_PAGE_URL,
    HOME_PAGE_URL,
    HR_HOME_PAGE_URL,
    SUPPORT_PAGE_URL,
} from 'components/App/components/routes';
import MenuSideButton from './ui/MenuSideButton';
import SubMenuSideButton from './ui/SubMenuSideButton';
import { SideBarMode } from './hooks';
import {
    checkBudgetAdminSelected,
    checkBudgetAsResourceSelected,
    checkBudgetOverviewSelected,
    checkBackOfficeChartsTemplatesSelected,
    checkDashboardsSelected,
    checkExploreSelected,
    checkHomeSelected,
    checkHrSettingsSelected,
    checkHrSimulationHomeSelected,
    checkHrSimulationItemSelected,
    checkIndicatorsSelected,
    checkLocationInBackOffice,
    checkPayStateHomeSelected,
    checkPayStateItemSelected,
    checkRetrospectiveHomeSelected,
    checkRetrospectiveItemSelected,
} from './locationHelpers';
import { getBudgetSettingsUrl, getDecisionHome } from './helpers';

type Props = {
    sideBarMode: SideBarMode;
    isExpanded: boolean;
    setExpanded: DispatchState<boolean>;
    isAuthenticated: boolean;
    homePageUrl: string;
};

/**
 * This contains the main links in the middle of the side bar.
 *
 * Beware, the links available are different for Essentiel and for Decision.
 * When the user is not logged in, no link are visible.
 */
export default function SideBarItems({
    sideBarMode,
    isExpanded,
    setExpanded,
    isAuthenticated,
    homePageUrl,
}: Props) {
    const { t } = useTranslation();
    const isSupportUser = useUserHasRole(SUPPORT);
    const isAdminCategories = useIsAdminCategories();
    const hasAccessCadrageGestion = useHasAccessCadrageGestion();
    const hasAccessBudgetsGestion = useHasAccessBudgetsGestion();
    const hasDafAccess = useHasDafAccess();
    const isDecisionUser = useUserHasRole(DECISION_USER) || isSupportUser;
    const isHumanResourcesUser = useUserHasRole(HUMAN_RESOURCES_USER);
    const isExploreUser = useIsExploreUser() || isSupportUser;
    const hasDecision = useClientHasModule(DECISION) || isSupportUser;
    const hasDecisionEssential = useIsEssential();
    const hasBudget = useClientHasModule(BUDGET);
    const hasBackOffice = useClientHasModule(BACK_OFFICE) && isSupportUser;
    const hasHumanResources = useClientHasModule(HUMAN_RESOURCES);

    const currentYear = new Date().getFullYear().toString();

    const { pathname } = useLocation();
    // HR module uses a subpath to identify its pages
    const subLocation = pathname.split('/')[1];
    const subHrLocation = pathname.split('/')[2];

    const isHomeSelected = checkHomeSelected(pathname);
    const isDashboardsSelected = checkDashboardsSelected(pathname);
    const isIndicatorsSelected = checkIndicatorsSelected(subLocation);
    const isExploreSelected = checkExploreSelected(subLocation);

    const isLocationInDecision =
        isDashboardsSelected || isIndicatorsSelected || isExploreSelected;

    const isLocationInBudget =
        checkBudgetAsResourceSelected(subLocation) ||
        checkBudgetOverviewSelected(subLocation) ||
        checkBudgetAdminSelected(subLocation);

    const isLocationInBackOffice = checkLocationInBackOffice(subLocation);

    const isPayStateSelected =
        checkPayStateHomeSelected(subHrLocation) ||
        checkPayStateItemSelected(subHrLocation);

    const isRetrospectiveSelected =
        checkRetrospectiveHomeSelected(subHrLocation) ||
        checkRetrospectiveItemSelected(subHrLocation);

    const isHrSimulationSelected =
        checkHrSimulationHomeSelected(subHrLocation) ||
        checkHrSimulationItemSelected(subHrLocation);

    const isHrSettingsSelected = checkHrSettingsSelected(subHrLocation);

    const isLocationInHumanResources =
        isPayStateSelected ||
        isHrSimulationSelected ||
        isHrSettingsSelected ||
        isRetrospectiveSelected;

    const isBackOfficeChartsTemplatesSelected =
        checkBackOfficeChartsTemplatesSelected(pathname);

    const decisionHomePageUrl = getDecisionHome(homePageUrl);

    const hasBudgetSettingsAccess =
        isSupportUser ||
        isAdminCategories ||
        hasAccessCadrageGestion ||
        hasAccessBudgetsGestion;

    const hasBudgetSectionsAccess = hasBudgetSettingsAccess || hasDafAccess;

    if (!isAuthenticated) return null;

    const onSubMenuClick = (
        eventName: string,
        eventType: 'common' | 'budget' = 'common',
    ) => {
        setExpanded(false);
        if (eventType === 'budget') {
            trackEventBudget(eventName);
        } else {
            trackEvent(eventName);
        }
    };

    return (
        <Grid container direction="column">
            <MenuSideButton
                sideBarMode={sideBarMode}
                icon={<Home />}
                text={t('modules.home')}
                to={HOME_PAGE_URL}
                isSideBarExpanded={isExpanded}
                setExpanded={setExpanded}
                isSelected={isHomeSelected}
            />
            <MenuSideButton
                sideBarMode={sideBarMode}
                icon={<DashboardIcon />}
                text={t('header.decision')}
                isSideBarExpanded={isExpanded}
                setExpanded={setExpanded}
                isAvailable={hasDecision}
                isSelected={isLocationInDecision}
                to={decisionHomePageUrl}
                className="sidebar-decision-button"
            >
                {isDecisionUser && (!hasDecisionEssential || isSupportUser) && (
                    <>
                        <SubMenuSideButton
                            text={t('header.dashboards')}
                            className="e2e-header-button-library"
                            onClick={() => setExpanded(false)}
                            to={DASHBOARDS_PAGE_URL}
                            selected={
                                isDashboardsSelected && homePageUrl !== pathname
                            }
                        />
                        <SubMenuSideButton
                            className="sidebar-indicators-icon-click"
                            text={t('header.indicators')}
                            to="/indicators"
                            onClick={() => setExpanded(false)}
                            selected={isIndicatorsSelected}
                        />

                        {isExploreUser && (
                            <SubMenuSideButton
                                text={t('header.explore')}
                                to="/explore"
                                onClick={() => setExpanded(false)}
                                selected={isExploreSelected}
                                className="sidebar-explore-button"
                            />
                        )}
                    </>
                )}
            </MenuSideButton>
            <MenuSideButton
                sideBarMode={sideBarMode}
                icon={<AccountBalanceIcon />}
                text={t('header.budget')}
                to={BUDGET_HOME_PAGE_URL}
                isSideBarExpanded={isExpanded}
                setExpanded={setExpanded}
                isAvailable={hasBudget}
                isSelected={isLocationInBudget}
                onClick={() => {
                    trackEventBudget(
                        TRACKABLE_EVENTS.budget.ux.homeBudgetClick,
                    );
                }}
                className="sidebar-budget-button"
            >
                {hasBudgetSectionsAccess && (
                    <>
                        <SubMenuSideButton
                            text={t('header.budgetWriting')}
                            onClick={() =>
                                onSubMenuClick(
                                    TRACKABLE_EVENTS.budget.ux.homeBudgetClick,
                                    'budget',
                                )
                            }
                            to={BUDGET_HOME_PAGE_URL}
                            selected={checkBudgetAsResourceSelected(
                                subLocation,
                            )}
                        />
                        {(hasDafAccess || isSupportUser) && (
                            <SubMenuSideButton
                                text={t('header.dafView')}
                                to={`${BUDGET_OVERVIEW}/finance_ratios`}
                                selected={checkBudgetOverviewSelected(
                                    subLocation,
                                )}
                                onClick={() =>
                                    onSubMenuClick(
                                        TRACKABLE_EVENTS.budget.ux.vueDafClick,
                                        'budget',
                                    )
                                }
                            />
                        )}
                        {hasBudgetSettingsAccess && (
                            <SubMenuSideButton
                                text={t('header.budgetSettings')}
                                to={getBudgetSettingsUrl(
                                    isAdminCategories,
                                    hasAccessCadrageGestion,
                                    isSupportUser,
                                )}
                                onClick={() =>
                                    onSubMenuClick(
                                        TRACKABLE_EVENTS.budget.ux
                                            .settingsBudgetClick,
                                        'budget',
                                    )
                                }
                                selected={checkBudgetAdminSelected(subLocation)}
                            />
                        )}
                    </>
                )}
            </MenuSideButton>
            <MenuSideButton
                sideBarMode={sideBarMode}
                icon={<PeopleAltIcon />}
                text={t('modules.humanResources')}
                isSideBarExpanded={isExpanded}
                setExpanded={setExpanded}
                isAvailable={hasHumanResources}
                isSelected={isLocationInHumanResources}
                to={`${HR_HOME_PAGE_URL}/simulation_home`}
                className="sidebar-prh-button"
            >
                {isHumanResourcesUser &&
                    (hasHumanResources || isSupportUser) && (
                        <>
                            <SubMenuSideButton
                                text={t('header.simulations')}
                                to={`${HR_HOME_PAGE_URL}/simulation_home`}
                                onClick={() => setExpanded(false)}
                                selected={isHrSimulationSelected}
                            />
                            <SubMenuSideButton
                                text={t('header.payState')}
                                onClick={() => setExpanded(false)}
                                to={`${HR_HOME_PAGE_URL}/pay_state_home/${currentYear}`}
                                selected={isPayStateSelected}
                            />
                            <SubMenuSideButton
                                text={t('header.retrospectives')}
                                to={`${HR_HOME_PAGE_URL}/retrospective_home`}
                                onClick={() => setExpanded(false)}
                                selected={isRetrospectiveSelected}
                            />
                            <SubMenuSideButton
                                text={t('header.parameters')}
                                to={`${HR_HOME_PAGE_URL}/settings`}
                                onClick={() => setExpanded(false)}
                                selected={isHrSettingsSelected}
                            />
                        </>
                    )}
            </MenuSideButton>
            {hasBackOffice && (
                <MenuSideButton
                    sideBarMode={sideBarMode}
                    icon={<DescriptionIcon />}
                    text={t('modules.backOffice')}
                    to={SUPPORT_PAGE_URL}
                    isSideBarExpanded={isExpanded}
                    setExpanded={setExpanded}
                    isAvailable={hasBackOffice}
                    isSelected={
                        isLocationInBackOffice ||
                        isBackOfficeChartsTemplatesSelected
                    }
                >
                    <SubMenuSideButton
                        text={t('header.clients')}
                        onClick={() => setExpanded(false)}
                        to={SUPPORT_PAGE_URL}
                        selected={isLocationInBackOffice}
                    />
                    <SubMenuSideButton
                        text={t('support.chartsTemplates.title')}
                        to={CHARTS_TEMPLATES_PATHNAME}
                        onClick={() => setExpanded(false)}
                        selected={isBackOfficeChartsTemplatesSelected}
                    />
                </MenuSideButton>
            )}
        </Grid>
    );
}
